import { CompensationRollupValue } from "@/components/sensitive-value/CompensationRollupValue.tsx";
import { SensitiveValue } from "@/components/sensitive-value/SensitiveValue.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { useEmployeeCompensationSummary } from "@/queries/employees/use-employee-compensations.query.ts";
import { useAppConfig } from "@/queries/settings/use-app.query.ts";
import {
  ApiEmployeeCompensationPerTypes,
  getEmployeeCompensationPerTypes,
  getEmployeeCompensationPerTypeTitle,
} from "@/types/employees/employee-compensation.types.ts";
import {
  Box,
  Button,
  ButtonLink,
  DotSeparator,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  MetricTile,
  Skeleton,
  Stack,
  Text,
  Title,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@skailer-com/uikit";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

interface EmployeeCompensationSummarySectionProps {
  employeeId: number;
  enabled: boolean;
}

interface CompensationMetricProps {
  title: string;
  tooltip: string;
  value: string | null;
  currencyCode: string;
  period: string;
  isIncomplete: boolean;
  isEstimated: boolean;
  missingLineTitles: string[];
  valuesVisible: boolean;
}

function CompensationMetric({
  title,
  tooltip,
  value,
  currencyCode,
  period,
  isIncomplete,
  isEstimated,
  missingLineTitles,
  valuesVisible,
}: CompensationMetricProps) {
  const { renderCurrency } = useFormatter();

  return (
    <MetricTile
      className="min-w-0 flex-1"
      title={title}
      titleIcon={
        <Tooltip>
          <TooltipTrigger>
            <FontAwesomeIcon icon="fa-light fa-circle-question" className="text-secondary" />
          </TooltipTrigger>
          <TooltipContent>{tooltip}</TooltipContent>
        </Tooltip>
      }
      valueSlot={
        <CompensationRollupValue
          value={value === null ? null : renderCurrency(value, currencyCode)}
          isIncomplete={isIncomplete}
          isEstimated={isEstimated}
          missingLineTitles={missingLineTitles}
          defaultVisible={valuesVisible}
          hideToggle
          textClassName="text-2xl"
        />
      }
      caption={period}
    />
  );
}

export function EmployeeCompensationSummarySection({
  employeeId,
  enabled,
}: EmployeeCompensationSummarySectionProps) {
  const { t } = useTranslation();
  const { renderCurrency, renderTimeAgo } = useFormatter();
  const { data: appConfig } = useAppConfig();
  const [perType, setPerType] = useState(ApiEmployeeCompensationPerTypes.monthly);
  const configuredVisibility = appConfig?.ui.settings.show_full_salary ?? false;
  const [visibility, setVisibility] = useState({
    value: configuredVisibility,
    configuredVisibility,
  });
  const valuesVisible =
    visibility.configuredVisibility === configuredVisibility
      ? visibility.value
      : configuredVisibility;
  const perTypeOptions = getEmployeeCompensationPerTypes().filter(
    (option) =>
      option.id === ApiEmployeeCompensationPerTypes.monthly ||
      option.id === ApiEmployeeCompensationPerTypes.yearly
  );
  const selectedPerType = perTypeOptions.find((option) => option.id === perType);
  const { data: summary, isLoading } = useEmployeeCompensationSummary(employeeId, perType, enabled);

  const sortedLines = useMemo(
    () => [...(summary?.lines ?? [])].sort((first, second) => first.sort - second.sort),
    [summary?.lines]
  );
  const missingLineTitles = useMemo(() => {
    const missingIds = new Set([
      ...(summary?.missing_fx_line_ids ?? []),
      ...(summary?.missing_amount_line_ids ?? []),
    ]);
    return sortedLines
      .filter((line) => missingIds.has(line.id))
      .map((line) => line.component.title);
  }, [sortedLines, summary?.missing_amount_line_ids, summary?.missing_fx_line_ids]);

  if (!enabled) return null;

  if (isLoading) {
    return <Skeleton className="h-64 w-full" />;
  }

  if (!summary) return null;

  const periodTitle = getEmployeeCompensationPerTypeTitle(summary.rollups.per_type);
  const updatedTitle = `${t("reporting:last_updated")} ${renderTimeAgo(summary.datetime_updated_at)}`;
  const metricProps = {
    currencyCode: summary.rollups.currency_code,
    period: periodTitle,
    isIncomplete: summary.is_rollup_incomplete,
    isEstimated: summary.is_rollup_estimated,
    missingLineTitles,
    valuesVisible,
  };

  return (
    <Box variant="border">
      <Stack className="px-2" gap="lg">
        <Stack direction="horizontal" items="center" justify="between" gap="md">
          <Title header={t("core:compensation")} size="md" />
          <Stack direction="horizontal" items="center" gap="sm">
            <DropdownMenu
              trigger={
                <Button
                  variant="secondary"
                  size="sm"
                  rightIcon={<FontAwesomeIcon icon="fa-light fa-chevron-down" />}
                >
                  {selectedPerType?.title ?? t("common:period")}
                </Button>
              }
            >
              {perTypeOptions.map((option) => (
                <DropdownMenuItem
                  key={option.id}
                  title={option.title}
                  isSelected={option.id === perType}
                  onClick={() => setPerType(option.id as ApiEmployeeCompensationPerTypes)}
                />
              ))}
            </DropdownMenu>
            <Button
              variant="secondary"
              size="sm"
              leftIcon={
                <FontAwesomeIcon
                  icon={valuesVisible ? "fa-light fa-eye-slash" : "fa-light fa-eye"}
                />
              }
              onClick={() => setVisibility({ value: !valuesVisible, configuredVisibility })}
            >
              {t(valuesVisible ? "common:hide_all" : "common:show_all")}
            </Button>
          </Stack>
        </Stack>
        <Stack gap={"sm"}>
          <Stack direction="horizontal" gap="sm">
            <CompensationMetric
              {...metricProps}
              title={t("core:compensation_summary_total_cost")}
              tooltip={t("core:compensation_summary_total_cost_description")}
              value={summary.rollups.total_cost_actual_amount}
            />
            <CompensationMetric
              {...metricProps}
              title={t("core:compensation_gross_rollup")}
              tooltip={t("core:compensation_summary_gross_description")}
              value={summary.rollups.gross_amount}
            />
            <CompensationMetric
              {...metricProps}
              title={t("core:compensation_summary_net")}
              tooltip={t("core:compensation_summary_net_description")}
              value={summary.rollups.net_actual_amount}
            />
          </Stack>

          {sortedLines.length > 0 && (
            <Box className="rounded-sm bg-dark/5 p-4">
              <Stack>
                <Title
                  size={"xs"}
                  header={t("core:compensation_summary_breakdown")}
                  paddingBottom
                />
                {sortedLines.map((line) => (
                  <Stack
                    key={line.id}
                    direction="horizontal"
                    justify="between"
                    gap="md"
                    className="border-b border-dark/10 py-2 last:border-b-0"
                  >
                    <Stack>
                      <DotSeparator className="inline-flex items-center">
                        <Text className="font-semibold">{line.component.title}</Text>
                        {getEmployeeCompensationPerTypeTitle(line.per_type)}
                      </DotSeparator>
                      <DotSeparator className="inline-flex items-center">
                        <SensitiveValue
                          value={
                            line.amount_net === null
                              ? "-"
                              : renderCurrency(line.amount_net, line.currency_code)
                          }
                          defaultVisible={valuesVisible}
                          hideToggle
                          textClassName="text-secondary"
                        />
                        <Text className="text-secondary">{t("core:compensation_summary_net")}</Text>
                      </DotSeparator>
                      <DotSeparator className="inline-flex items-center">
                        <SensitiveValue
                          value={
                            line.amount_gross === null
                              ? "-"
                              : renderCurrency(line.amount_gross, line.currency_code)
                          }
                          defaultVisible={valuesVisible}
                          hideToggle
                          textClassName="text-secondary"
                        />
                        <Text className="text-secondary">
                          {t("core:compensation_gross_rollup")}
                        </Text>
                      </DotSeparator>
                    </Stack>
                    <Text className="shrink-0 self-center text-secondary">{updatedTitle}</Text>
                  </Stack>
                ))}
              </Stack>
            </Box>
          )}

          <ButtonLink
            href={`/employees/${employeeId}/work-info`}
            variant="secondary"
            rightIcon={<FontAwesomeIcon icon="fa-light fa-angle-right" />}
            className="w-fit"
          >
            {t("common:view")}
          </ButtonLink>
        </Stack>
      </Stack>
    </Box>
  );
}
